import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Sun, 
  Moon, 
  User, 
  LogOut, 
  Settings, 
  Bell, 
  Shield,
  Menu,
  X,
  Activity,
  Gift,
  BarChart3,
  Users,
  Share2,
  History,
  LayoutDashboard
} from 'lucide-react';

export const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="glass sticky top-0 z-50 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-sm bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="font-display text-xl font-bold tracking-tight uppercase">
                SportsAnalysisAB
              </span>
              <span className="hidden sm:block text-xs text-muted-foreground tracking-wider">
                ALBERTA SPORTS ANALYTICS
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/board" 
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors uppercase tracking-wide"
            >
              Games
            </Link>
            <Link 
              to="/stats" 
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors uppercase tracking-wide flex items-center gap-1"
            >
              <Users className="w-3.5 h-3.5" />
              Stats
            </Link>
            <Link 
              to="/analysis" 
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors uppercase tracking-wide flex items-center gap-1"
            >
              <BarChart3 className="w-3.5 h-3.5" />
              Analysis
            </Link>
            {user && (
              <Link 
                to="/alerts" 
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors uppercase tracking-wide"
              >
                Alerts
              </Link>
            )}
            {isAdmin && (
              <Link 
                to="/admin" 
                className="text-sm font-medium text-red-400 hover:text-red-300 transition-colors uppercase tracking-wide"
              >
                Admin
              </Link>
            )}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-sm"
              data-testid="theme-toggle"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-sm gap-2" data-testid="user-menu">
                    {user.picture ? (
                      <img 
                        src={user.picture} 
                        alt={user.name} 
                        className="w-6 h-6 rounded-full"
                      />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                    <span className="hidden sm:inline text-sm">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="dashboard-menu-item">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    My Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/notifications')} data-testid="notifications-menu-item">
                    <Bell className="w-4 h-4 mr-2" />
                    Notifications
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/predictions')} data-testid="predictions-menu-item">
                    <History className="w-4 h-4 mr-2" />
                    My Predictions
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/referral')} data-testid="referral-menu-item">
                    <Gift className="w-4 h-4 mr-2" />
                    Referral Program
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/share')} data-testid="share-menu-item">
                    <Share2 className="w-4 h-4 mr-2" />
                    Share & Earn
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/admin-dashboard')} data-testid="admin-dashboard-menu-item">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Admin Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-menu-item">
                        <Shield className="w-4 h-4 mr-2" />
                        Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  className="rounded-sm hidden sm:inline-flex"
                  onClick={() => navigate('/login')}
                  data-testid="login-btn"
                >
                  Login
                </Button>
                <Button 
                  className="rounded-sm bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wider text-xs px-4"
                  onClick={() => navigate('/signup')}
                  data-testid="signup-btn"
                >
                  Sign Up
                </Button>
              </div>
            )}

            {/* Mobile menu toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden rounded-sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-white/5 py-4 px-4">
          <div className="flex flex-col gap-3">
            <Link 
              to="/board" 
              className="text-sm font-medium py-2 hover:text-foreground transition-colors uppercase tracking-wide"
              onClick={() => setMobileMenuOpen(false)}
            >
              Games
            </Link>
            <Link 
              to="/stats" 
              className="text-sm font-medium py-2 hover:text-foreground transition-colors uppercase tracking-wide"
              onClick={() => setMobileMenuOpen(false)}
            >
              Stats
            </Link>
            <Link 
              to="/analysis" 
              className="text-sm font-medium py-2 hover:text-foreground transition-colors uppercase tracking-wide"
              onClick={() => setMobileMenuOpen(false)}
            >
              Analysis
            </Link>
            {user && (
              <>
                <Link 
                  to="/alerts" 
                  className="text-sm font-medium py-2 hover:text-foreground transition-colors uppercase tracking-wide"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Alerts
                </Link>
                <Link 
                  to="/notifications" 
                  className="text-sm font-medium py-2 hover:text-foreground transition-colors uppercase tracking-wide"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Notifications
                </Link>
              </>
            )}
            {isAdmin && (
              <Link 
                to="/admin" 
                className="text-sm font-medium py-2 text-red-400 hover:text-red-300 transition-colors uppercase tracking-wide"
                onClick={() => setMobileMenuOpen(false)}
              >
                Admin
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
